import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PARTNER_TIERS, PARTNER_BENEFITS } from "@/data/legal";
import { BRAND } from "@/data/product";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { api, formatErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Tag, Sparkles, Package, Users, Headphones,
  CheckCircle2, Loader2, Send, Mail,
} from "lucide-react";

const ICONS = { tag: Tag, sparkles: Sparkles, package: Package, users: Users, headphones: Headphones };

export default function Partners() {
  const [form, setForm] = useState({
    full_name: "",
    salon_name: "",
    city: "",
    phone: "",
    email: "",
    socials: "",
    monthly_clients: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partners/apply", form);
      trackEvent("Lead", { source: "partner_application", content_name: "Partner application" });
      setDone(true);
      toast.success("Candidature envoyée", { description: "Nous revenons vers vous très vite." });
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF9] text-[#2C2420]">
      <header className="border-b border-[#EADBD1] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="partners-home-link">
            <span className="font-serif text-xl md:text-2xl tracking-tight">French Curl</span>
            <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#C58C85]">Vibes</span>
          </Link>
          <Link to="/" className="text-sm text-[#6B5D54] hover:text-[#2C2420] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-12">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Programme professionnel</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] mt-4 text-[#2C2420]">
            Devenez <span className="italic text-[#C58C85]">partenaire</span>
            <br />
            French Curl Vibes.
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#6B5D54] max-w-2xl mx-auto">
            Coiffeuse, tresseuse, gérante de salon ? Rejoignez le réseau French Curl Vibes et proposez à vos clientes
            des mèches French Curl de qualité premium tout en bénéficiant d&apos;avantages exclusifs.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <a
              href="#candidature"
              className="inline-flex items-center justify-center rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white px-7 h-14 text-base transition-colors shadow-lg shadow-[#2C2420]/20"
              data-testid="partners-hero-cta"
            >
              Postuler maintenant
              <ArrowRight className="h-4 w-4 ml-2" />
            </a>
            <a
              href="#niveaux"
              className="inline-flex items-center justify-center rounded-full border border-[#EADBD1] hover:border-[#2C2420] text-[#2C2420] px-7 h-14 text-base transition-colors"
            >
              Voir les niveaux
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-20 bg-[#F5EFE6]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Pourquoi rejoindre</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">Vos avantages.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PARTNER_BENEFITS.map((b, i) => {
              const Icon = ICONS[b.icon] || Tag;
              return (
                <div key={i} className="rounded-3xl bg-white border border-[#EADBD1] p-6 hover:-translate-y-1 transition-transform" data-testid={`partner-benefit-${i}`}>
                  <span className="h-10 w-10 rounded-full bg-[#F5EFE6] grid place-items-center">
                    <Icon className="h-4 w-4 text-[#C58C85]" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-lg mt-4 text-[#2C2420] leading-tight">{b.title}</h3>
                  <p className="text-xs text-[#6B5D54] mt-2 leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section id="niveaux" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Programme évolutif</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">Nos niveaux.</h2>
            <p className="text-[#6B5D54] mt-4">Niveau réévalué chaque trimestre selon le volume de commandes.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {PARTNER_TIERS.map((t, i) => (
              <article
                key={t.id}
                data-testid={`tier-${t.id}`}
                className="relative rounded-3xl bg-white border border-[#EADBD1] p-7 md:p-9 overflow-hidden hover:-translate-y-1 transition-transform"
              >
                <span className="absolute top-7 right-7 text-4xl" aria-hidden="true">{t.emoji}</span>
                <div className="absolute -top-1 left-7 h-2 w-16 rounded-b-full" style={{ background: t.accent }} />
                <p className="text-xs uppercase tracking-[0.25em] font-medium" style={{ color: t.accent }}>
                  Niveau {i + 1}
                </p>
                <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#2C2420]">{t.label}</h3>
                <p className="text-sm text-[#6B5D54] mt-3 leading-relaxed">{t.tagline}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#F5EFE6] px-3 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-[#6B5D54] font-medium">Condition</span>
                  <span className="text-xs text-[#2C2420]">{t.condition}</span>
                </div>
                <Separator className="my-5 bg-[#EADBD1]" />
                <ul className="space-y-2.5">
                  {t.advantages.map((adv, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-[#2C2420]">
                      <CheckCircle2 className="h-4 w-4 text-[#25D366] mt-0.5 flex-shrink-0" />
                      <span>{adv}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="candidature" className="py-20 md:py-28 bg-[#2C2420] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Candidature</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">
              Rejoignez l&apos;aventure.
            </h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">
              Remplissez le formulaire ci-dessous, nous étudions votre demande et revenons vers vous sous quelques jours.
            </p>
          </div>

          {done ? (
            <div className="mt-10 rounded-3xl bg-white text-[#2C2420] p-10 text-center" data-testid="partner-success">
              <CheckCircle2 className="h-14 w-14 text-[#25D366] mx-auto" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mt-4">Candidature reçue.</h3>
              <p className="text-[#6B5D54] mt-3">
                Merci ! Nous reviendrons vers vous sous quelques jours à <span className="font-medium">{form.email}</span>.
              </p>
              <Link to="/">
                <Button className="mt-6 rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white px-6 h-12">
                  Retour à l&apos;accueil
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-3xl bg-white text-[#2C2420] p-7 md:p-10 space-y-5" data-testid="partner-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nom et prénom *" value={form.full_name} onChange={set("full_name")} required testid="partner-fullname" />
                <Field label="Nom du salon / activité" value={form.salon_name} onChange={set("salon_name")} testid="partner-salon" />
                <Field label="Ville *" value={form.city} onChange={set("city")} required testid="partner-city" />
                <Field label="Téléphone *" value={form.phone} onChange={set("phone")} required type="tel" testid="partner-phone" />
                <Field label="Email *" value={form.email} onChange={set("email")} required type="email" testid="partner-email" />
                <Field label="Réseaux sociaux pro" value={form.socials} onChange={set("socials")} placeholder="@instagram, TikTok…" testid="partner-socials" />
              </div>
              <Field label="Nombre moyen de clientes/mois" value={form.monthly_clients} onChange={set("monthly_clients")} testid="partner-clients" />
              <div>
                <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">Présentation / commentaire</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="w-full px-4 py-3 rounded-2xl bg-[#FDFBF9] border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm resize-none"
                  data-testid="partner-message"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white h-14 text-base" data-testid="partner-submit-btn">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi…</> : <><Send className="h-4 w-4 mr-2" /> Envoyer ma candidature</>}
              </Button>
              <p className="text-[11px] text-[#6B5D54] text-center">
                En soumettant ce formulaire, vous acceptez d&apos;être recontacté(e) par notre équipe.
              </p>
            </form>
          )}

          <div className="mt-10 text-center">
            <p className="text-sm text-white/60">
              Question sur le programme ?{" "}
              <a href={`mailto:${BRAND.partnersEmail}`} className="text-[#C58C85] hover:underline inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> {BRAND.partnersEmail}
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.22em] text-[#6B5D54] mb-1.5 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full h-11 px-4 rounded-xl bg-[#FDFBF9] border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}
